import { useMemo, useState } from 'react';
import { Country } from '~/Api/Country';
import { CzMaxPrice, Product } from '~/libs/infrastructure/schemas/czMaxPrice';
import { isSuklReferenceProduct, PricedProduct, Product as GeneralProduct } from '~/Product';
import { resolveProductGroup, GroupTypes } from '~/PriceReferencing';
import ReferenceBasketVersion from '~/Api/PriceReferencing/ReferenceBasketVersion';
import { ProductRow, ProductValues } from '../components/czMaxPrice/Table';

const compareBasketProducts = (a: ProductRow, b: ProductRow): number => {
	if (a.product !== null && b.product === null) return -1;
	if (a.product === null && b.product !== null) return 1;

	const comparison = a.country.alpha2.localeCompare(b.country.alpha2);
	if (comparison !== 0) return comparison;

	if (a.product !== null && b.product !== null) {
		return a.product.product.brandName.localeCompare(b.product.product.brandName);
	}
	return 0;
};

export function useProducts(
	czMaxPrice: CzMaxPrice | null,
	versions: ReferenceBasketVersion[] | null,
	showAll: boolean
) {
	const [checkedRows, setCheckedRows] = useState<GeneralProduct[]>([]);
	const [lastSelectedProductId, setLastSelectedProductId] = useState<string | null>(null);

	const filtered = useMemo(() => {
		const version =
			versions !== null && czMaxPrice !== null
				? versions.find((v) => v.id === czMaxPrice.reference.referenceBasketVersion) ?? null
				: null;
		const countries = version?.referenceCountries ?? [];
		const products = czMaxPrice !== null && version !== null ? czMaxPrice.products : [];
		const nonsetCountries = new Set(countries);
		const usedForCalculationIds = czMaxPrice?.finalCalculation.fromProducts.map((p) => p.id) ?? [];

		const czechProducts: ProductRow[] = [];
		const suklReferencedProducts: ProductRow[] = [];
		const otherProducts: ProductRow[] = [];
		let alternativeProductsCount = 0;

		products.forEach((product) => {
			const group = resolveProductGroup(product.product, countries);
			const isAlternative = isSuklReferenceProduct(product);
			alternativeProductsCount += isAlternative || group.type === GroupTypes.other ? 1 : 0;

			if (!showAll && isAlternative) return;

			const index = usedForCalculationIds.indexOf(product.product.id);
			const lowestPriceIndex = index >= 0 ? index : null;

			if (group.type === GroupTypes.czech) {
				czechProducts.push({ country: group.country, product, lowestPriceIndex });
				return;
			}
			if (group.type === GroupTypes.referenced) {
				suklReferencedProducts.push({ country: group.country, product, lowestPriceIndex });
				nonsetCountries.delete(group.country);
				return;
			}
			if (showAll) {
				otherProducts.push({ country: group.country, product, lowestPriceIndex });
			}
		});

		if (czechProducts.length === 0) {
			czechProducts.push({
				country: new Country('CZ', 'Czech Republic'),
				product: null,
				lowestPriceIndex: null,
			});
		}

		countries.forEach((country) => {
			if (nonsetCountries.has(country)) {
				suklReferencedProducts.push({ country, product: null, lowestPriceIndex: null });
			}
		});

		czechProducts.sort(compareBasketProducts);
		suklReferencedProducts.sort(compareBasketProducts);
		otherProducts.sort(compareBasketProducts);

		const pricedProducts = [...czechProducts, ...suklReferencedProducts, ...otherProducts]
			.map((row) => (row.product !== null ? new PricedProduct(row.product, row.product.product, null) : null))
			.filter((p): p is PricedProduct<Product> => p !== null);

		return {
			pricedProducts,
			czechProducts,
			suklReferencedProducts,
			otherProducts,
			alternativeProductsCount,
			referenceBasketVersion: version,
		};
	}, [czMaxPrice, versions, showAll]);

	function checkRow(product: GeneralProduct, checked: boolean) {
		setCheckedRows((prev) => (checked ? [...prev, product] : prev.filter((row) => row.id !== product.id)));
	}

	function clearCheckedRows() {
		setCheckedRows([]);
	}

	function computeSelectionUpdate(
		item: GeneralProduct,
		selected: boolean,
		range: boolean,
		productValues: { [id: string]: ProductValues }
	): { [id: string]: ProductValues } {
		const updatedValues = { ...productValues };

		if (range && lastSelectedProductId !== null) {
			const selectedIndex = filtered.pricedProducts.findIndex((p) => p.product.id === item.id);
			const lastIndex = filtered.pricedProducts.findIndex((p) => p.product.id === lastSelectedProductId);

			if (selectedIndex >= 0 && lastIndex >= 0) {
				const startIndex = Math.min(selectedIndex, lastIndex);
				const endIndex = Math.max(selectedIndex, lastIndex);
				for (let i = startIndex; i <= endIndex; i += 1) {
					const id = filtered.pricedProducts[i].product.id;
					updatedValues[id] = { ...updatedValues[id], selected };
				}
			}
		} else {
			updatedValues[item.id] = { ...updatedValues[item.id], selected };
		}

		return updatedValues;
	}

	function recordLastSelected(item: GeneralProduct) {
		setLastSelectedProductId(item.id);
	}

	return {
		...filtered,
		checkedRows,
		lastSelectedProductId,
		checkRow,
		clearCheckedRows,
		computeSelectionUpdate,
		recordLastSelected,
	};
}
